import { isConditionEffectivelyValid } from '../../../../common/utils/isConditionEffectivelyValid'
import { ConditionGroupType, ConditionType } from '../../contexts/PatternTreeContext'
import {
  BadgeSpec,
  ChartKey,
  CompileWarning,
  ConstraintBundle,
  Disjunction,
  Equality,
  GapConstraint,
  OrderEdge,
  SeriesId,
  VarId,
} from '../types'
import { BarPlan, planAxis, requiredDepth } from './barMapping'
import {
  BuilderConditionLike,
  CompiledCondition,
  compileCondition,
  getChartKey,
} from './conditionCompiler'
import { BAND_ORDER, checkGroupStructure, emitInvariants } from './invariants'
import { resolveOperand } from './operands'

export interface GroupNode {
  groupId: string
  count: number
  members: Array<{ kind: 'condition'; conditionId: string } | { kind: 'group'; node: GroupNode }>
}

export interface SeriesInfo {
  indicatorName: string
  field: string
  params?: Record<string, unknown>
}

export interface ConstraintModel {
  charts: Record<
    ChartKey,
    {
      axisLength: number
      seriesIds: SeriesId[]
      seriesInfo: Record<SeriesId, SeriesInfo>
    }
  >
  edges: OrderEdge[]
  equalities: Equality[]
  gaps: GapConstraint[]
  disjunctions: Disjunction[]
  intervals: Record<VarId, { lo: number; hi: number }>
  groupTree: GroupNode[]
  conditionIds: string[]
  anchors: Record<string, { kind: 'fixed' } | { kind: 'window'; bars: number }>
  prereqEdges: Array<{ before: string; after: string }>
  warnings: CompileWarning[]
  badges: BadgeSpec[]
  /** windowed conditions whose inner disjunctions were first-option flattened
   *  by toBundle — a solve failure touching these degrades to 'unverified' */
  flattenedWindowIds: string[]
}

const collectConditions = (groups: ConditionGroupType[]): ConditionType[] => {
  const found: ConditionType[] = []
  const visit = (group: ConditionGroupType): void => {
    ;(group.conditions ?? []).forEach((child) => found.push(child))
    ;(group.conditionGroups ?? []).forEach(visit)
  }
  groups.forEach(visit)
  return found
}

const buildGroupTree = (groups: ConditionGroupType[], validIds: Set<string>): GroupNode[] =>
  groups.map((group) => {
    const conditionMembers = (group.conditions ?? [])
      .filter((child) => validIds.has(child.id))
      .map((child) => ({ kind: 'condition' as const, conditionId: child.id }))
    const groupMembers = buildGroupTree(group.conditionGroups ?? [], validIds).map((node) => ({
      kind: 'group' as const,
      node,
    }))
    const members = [...conditionMembers, ...groupMembers]
    // Invalid conditions are excluded from the preview, so cap the requirement
    // at what remains — the preview depicts the pattern as intended; the red
    // invalid glow + save gate already report the incomplete condition itself.
    const count = Math.min(group.count ?? members.length, members.length)
    return { groupId: group.id, count, members }
  })

const seriesParts = (seriesId: SeriesId): { hash: string; field: string } | undefined => {
  const dot = seriesId.indexOf('.')
  if (dot < 0) return undefined
  return { hash: seriesId.slice(0, dot), field: seriesId.slice(dot + 1) }
}

const mergeCompiled = (model: ConstraintModel, compiled: CompiledCondition): void => {
  model.edges.push(...compiled.edges)
  model.equalities.push(...compiled.equalities)
  model.gaps.push(...compiled.gaps)
  model.disjunctions.push(...compiled.disjunctions)
  model.warnings.push(...compiled.warnings)
  model.badges.push(...compiled.badges)
}

/** Flattens one anchor's compilation into a plain bundle (first option of any
 *  inner disjunction — best effort; windowed + lookback-some is a rare combo). */
const toBundle = (compiled: CompiledCondition): ConstraintBundle => {
  const bundle: ConstraintBundle = {
    edges: [...compiled.edges],
    equalities: [...compiled.equalities],
    gaps: [...compiled.gaps],
  }
  compiled.disjunctions.forEach((disjunction) => {
    const first = disjunction.options[0]
    if (first) {
      bundle.edges.push(...first.edges)
      bundle.equalities.push(...first.equalities)
      bundle.gaps.push(...first.gaps)
    }
  })
  return bundle
}

/**
 * Builds ONE global constraint model for the active side's groups: every chart
 * key gets its own bar axis and variable namespace, and group counts (which can
 * span chart keys) are represented once in groupTree for the solver.
 */
export const compilePattern = (groups: ConditionGroupType[]): ConstraintModel => {
  const model: ConstraintModel = {
    charts: {},
    edges: [],
    equalities: [],
    gaps: [],
    disjunctions: [],
    intervals: {},
    groupTree: [],
    conditionIds: [],
    anchors: {},
    prereqEdges: [],
    warnings: [],
    badges: [],
    flattenedWindowIds: [],
  }

  model.warnings.push(...checkGroupStructure(groups as never[]))

  const allConditions = collectConditions(groups)
  // Legacy 'price movement' conditions fail the effective-validity filter (the
  // type is unauthorable today), but saved docs still carry them — surface the
  // dead-at-runtime warning before filtering.
  allConditions.forEach((child) => {
    if (child.condition?.conditionType === 'price movement') {
      model.warnings.push({
        kind: 'neverFires',
        conditionId: child.id,
        message: "'Price movement' can never fire — it is not implemented in the alert engine.",
      })
    }
  })
  // chartDetails guard: legacy prod docs can predate the field (Pre-PR
  // checklist item 1); one malformed condition must not blank the whole panel.
  const validConditions = allConditions.filter(
    (child) => child.chartDetails && isConditionEffectivelyValid(child)
  )
  const validIds = new Set(validConditions.map((child) => child.id))
  model.groupTree = buildGroupTree(groups, validIds)

  // Plan one axis per chart, accounting for validity-window anchor shifts.
  const depthByChart = new Map<ChartKey, number>()
  const windowBarsByCondition = new Map<string, number>()
  validConditions.forEach((child) => {
    const chartKey = getChartKey(child.chartDetails)
    const lhs = resolveOperand(child.condition.LHS)
    const rhs = resolveOperand(child.condition.RHS)
    const windowBars = child.settings?.validityWindow?.candles ?? 0
    windowBarsByCondition.set(child.id, windowBars)
    const depth = requiredDepth(lhs, rhs, child.settings?.previousCandle) + windowBars
    depthByChart.set(chartKey, Math.max(depthByChart.get(chartKey) ?? 0, depth))
  })

  const planByChart = new Map<ChartKey, BarPlan>()
  depthByChart.forEach((depth, chartKey) => {
    planByChart.set(chartKey, planAxis(depth))
  })

  const seriesByChart = new Map<ChartKey, Map<string, Set<string>>>()
  const hashToNameByChart = new Map<ChartKey, Map<string, string>>()
  const seriesInfoByChart = new Map<ChartKey, Record<SeriesId, SeriesInfo>>()

  validConditions.forEach((child) => {
    const chartKey = getChartKey(child.chartDetails)
    const plan = planByChart.get(chartKey) as BarPlan
    model.conditionIds.push(child.id)

    // Track referenced series for invariants and rendering.
    const seriesMap = seriesByChart.get(chartKey) ?? new Map<string, Set<string>>()
    const nameMap = hashToNameByChart.get(chartKey) ?? new Map<string, string>()
    const infoMap = seriesInfoByChart.get(chartKey) ?? {}
    for (const side of [child.condition.LHS, child.condition.RHS]) {
      const operand = resolveOperand(side)
      if (!operand || operand.kind === 'constant' || !operand.seriesId) continue
      const parts = seriesParts(operand.seriesId)
      if (!parts) continue
      const fields = seriesMap.get(parts.hash) ?? new Set<string>()
      fields.add(parts.field)
      // Band-family indicators draw their WHOLE line set: referencing BB upper
      // alone would render one ambiguous floating line — the band shape is the
      // indicator's identity, and the companions stay gray (unpinned) while
      // the ordering invariant keeps the envelope geometrically honest.
      const bandFields = operand.indicatorName ? BAND_ORDER[operand.indicatorName] : undefined
      bandFields?.forEach((bandField) => {
        fields.add(bandField)
        infoMap[`${parts.hash}.${bandField}`] = {
          indicatorName: operand.indicatorName as string,
          field: bandField,
          params: side?.indicator?.params,
        }
      })
      seriesMap.set(parts.hash, fields)
      if (parts.hash !== 'candle' && operand.indicatorName) {
        nameMap.set(parts.hash, operand.indicatorName)
      }
      infoMap[operand.seriesId] = {
        indicatorName: operand.indicatorName ?? parts.hash,
        field: parts.field,
        params: side?.indicator?.params,
      }
    }
    seriesByChart.set(chartKey, seriesMap)
    hashToNameByChart.set(chartKey, nameMap)
    seriesInfoByChart.set(chartKey, infoMap)

    const builderCondition = child as unknown as BuilderConditionLike
    const windowBars = Math.min(windowBarsByCondition.get(child.id) ?? 0, plan.axisLength - 2)
    if (windowBars > 0) {
      model.anchors[child.id] = { kind: 'window', bars: windowBars }
      // Warnings/badges from the "now" anchor represent the condition.
      const representative = compileCondition(builderCondition, plan, plan.now)
      const representativeSize =
        representative.edges.length + representative.equalities.length + representative.gaps.length
      if (representative.disjunctions.length > 0) {
        // toBundle flattens inner disjunctions to their first option; record it
        // so a later solve failure degrades to 'unverified' instead of
        // asserting a conflict the runtime might not have (spec §7.7 fallback).
        model.flattenedWindowIds.push(child.id)
      }
      const options: ConstraintBundle[] = []
      for (let back = 0; back <= windowBars; back += 1) {
        const compiled = compileCondition(builderCondition, plan, plan.now - back)
        const bundle = toBundle(compiled)
        const bundleSize = bundle.edges.length + bundle.equalities.length + bundle.gaps.length
        // An anchor that overflowed the axis compiles to an EMPTY bundle
        // (compileCondition swallows the throw) — including it would give the
        // solver a trivially-satisfiable option. Skip it unless the condition
        // is genuinely constraint-free at every anchor.
        if (bundleSize === 0 && representativeSize > 0) continue
        options.push(bundle)
      }
      model.warnings.push(...representative.warnings)
      model.badges.push(...representative.badges)
      model.badges.push({
        conditionId: child.id,
        chartKey,
        kind: 'window',
        window: { from: plan.now - windowBars, to: plan.now },
      })
      if (options.length > 0) {
        model.disjunctions.push({
          options,
          provenance: { conditionId: child.id },
          label: 'window',
        })
      }
    } else {
      model.anchors[child.id] = { kind: 'fixed' }
      mergeCompiled(model, compileCondition(builderCondition, plan, plan.now))
    }

    ;(child.settings?.prerequisites ?? []).forEach((prerequisite) => {
      if (prerequisite.type === 'condition') {
        model.prereqEdges.push({ before: prerequisite.id, after: child.id })
      }
      model.badges.push({
        conditionId: child.id,
        chartKey,
        kind: 'prereq',
      })
    })
  })

  planByChart.forEach((plan, chartKey) => {
    const seriesMap = seriesByChart.get(chartKey) ?? new Map()
    const nameMap = hashToNameByChart.get(chartKey) ?? new Map()
    const invariants = emitInvariants(chartKey, seriesMap, nameMap, plan.axisLength)
    model.edges.push(...invariants.edges)
    Object.assign(model.intervals, invariants.intervals)

    const seriesIds: SeriesId[] = []
    seriesMap.forEach((fields: Set<string>, hash: string) => {
      fields.forEach((field) => seriesIds.push(`${hash}.${field}`))
    })
    model.charts[chartKey] = {
      axisLength: plan.axisLength,
      seriesIds,
      seriesInfo: seriesInfoByChart.get(chartKey) ?? {},
    }
  })

  return model
}
